import type {
  Character,
  Characteristics,
  DerivedStats,
  MovementRates,
  ProgressionTracking,
  Homeworld,
  Talent,
  StartingRoll,
  CharacterTrait
} from '../../../types/character';
import { TraitService } from '../../../services/TraitService';

/** `mechanics.ruleOverride` marking the Unnatural Speed trait in the catalogue. */
const UNNATURAL_SPEED_OVERRIDE = 'unnatural-speed';

// Resolve a StartingRoll to a numeric value (shared logic with characterCreation.ts)
function resolveStartingRoll(sr: StartingRoll, rollValue: number, toughnessBonus: number): number {
  if (sr.fateTable) {
    const entry = sr.fateTable.find((e) => rollValue <= e.max);
    return entry?.value ?? 0;
  }
  let result = rollValue;
  if (sr.usesToughnessBonus) result += 2 * toughnessBonus;
  if (sr.bonus) result += sr.bonus;
  return result;
}

// Progression calculation functions
export function calculateWoundsMaximum(
  characteristics: Characteristics,
  homeworld?: Homeworld,
  initialWoundsBonus?: number
): number {
  const tb = Math.floor(characteristics.toughness / 10);
  const woundsRoll = homeworld?.mechanics?.startingRolls?.find((r) => r.target === 'wounds');

  if (woundsRoll && initialWoundsBonus !== undefined) {
    return resolveStartingRoll(woundsRoll, initialWoundsBonus, tb);
  }

  // Fallback for characters without homeworld or without a wounds roll
  const strengthBonus = Math.floor(characteristics.strength / 10);
  const willpowerBonus = Math.floor(characteristics.willpower / 10);
  return strengthBonus + tb + willpowerBonus;
}

export function calculateFatigueMaximum(characteristics: Characteristics): number {
  const toughnessBonus = Math.floor(characteristics.toughness / 10);
  return toughnessBonus;
}

/**
 * Movement rates, all derived from Agility Bonus (RT p.19: half = AgB,
 * full = x2, charge = x3, run = x6).
 *
 * Unnatural Characteristic is deliberately NOT applied here. RT Core
 * (Adversaries and Aliens): "Note this Trait does not modify the creature's
 * movement. Movement is based on a creature's unmodified Agility Bonus."
 *
 * Doubling movement is the separate Unnatural Speed trait: "Double Agility
 * Bonus for purpose of determining movement."
 */
export function calculateMovementRates(
  characteristics: Characteristics,
  talents?: Talent[],
  traits?: CharacterTrait[]
): MovementRates {
  const baseAB = Math.floor(characteristics.agility / 10);

  // Unnatural Speed doubles the Agility Bonus for movement only.
  const hasUnnaturalSpeed = traits
    ? TraitService.hasRuleOverride(traits, UNNATURAL_SPEED_OVERRIDE)
    : false;
  const agilityBonus = hasUnnaturalSpeed ? baseAB * 2 : baseAB;

  const hasSprint = talents?.some((t) => t.id === 'sprint') ?? false;

  // Crawler: AB/2 for all movement; Quadruped: AB*2 for all movement
  const hasCrawler = traits?.some((t) => t.mechanics?.ruleOverride === 'crawler') ?? false;
  const hasQuadruped = traits?.some((t) => t.mechanics?.ruleOverride === 'quadruped') ?? false;

  let half = agilityBonus;
  if (hasCrawler) half = Math.floor(agilityBonus / 2);
  if (hasQuadruped) half = agilityBonus * 2;

  return {
    half,
    full: half * 2 + (hasSprint ? agilityBonus : 0),
    charge: half * 3,
    run: half * 6
  };
}

/**
 * Create default progression tracking data structure
 * Initializes all progression fields with appropriate defaults
 * @param characteristics - Character characteristics for calculating derived stats
 * @param homeworld - Optional homeworld for specific calculations
 * @param initialWoundsBonus - Optional initial wounds bonus (e.g. from Forge World roll)
 * @returns Complete ProgressionTracking object with all fields initialized
 */
export function createDefaultProgression(
  characteristics: Characteristics,
  homeworld?: Homeworld,
  initialWoundsBonus?: number,
  talents?: Talent[],
  traits?: CharacterTrait[]
): ProgressionTracking {
  const woundsMax = calculateWoundsMaximum(characteristics, homeworld, initialWoundsBonus);
  const fatigueMax = calculateFatigueMaximum(characteristics);
  const movementRates = calculateMovementRates(characteristics, talents, traits);

  return {
    currency: {
      thrones: 0
    },
    mentalState: {
      insanityPoints: 0,
      corruptionPoints: 0,
      insanityThreshold: 100,
      corruptionThreshold: 100,
      disorders: []
    },
    mutationTracking: {
      mutations: [],
      totalMutations: 0,
      lastMutationCheck: 0
    },
    psychicProfile: {
      isPsyker: false,
      psyRating: 0,
      psykerType: 'sanctioned',
      powers: [],
      phenomena: [],
      sustainedPowers: []
    },
    fatePoints: {
      current: 3,
      maximum: 3,
      destiny: 0
    },
    woundsTracking: {
      current: woundsMax,
      maximum: woundsMax,
      totalDamage: 0,
      locationDamage: [
        { location: 'head', currentDamage: 0, criticalEffects: [] },
        { location: 'rightArm', currentDamage: 0, criticalEffects: [] },
        { location: 'leftArm', currentDamage: 0, criticalEffects: [] },
        { location: 'body', currentDamage: 0, criticalEffects: [] },
        { location: 'rightLeg', currentDamage: 0, criticalEffects: [] },
        { location: 'leftLeg', currentDamage: 0, criticalEffects: [] }
      ],
      criticalDamage: [],
      permanentInjuries: [],
      unconscious: false,
      dead: false,
      firstAidApplied: false
    },
    fatigueTracking: {
      current: 0,
      maximum: fatigueMax
    },
    movementRates
  };
}

/**
 * Initialize progression tracking with default values for an existing character
 * Internal helper function that wraps createDefaultProgression
 * @param character - Character to initialize progression for
 * @returns Complete ProgressionTracking object
 */
export function initializeProgression(character: Character): ProgressionTracking {
  return createDefaultProgression(
    character.characteristics,
    character.homeworld,
    character.initialWoundsBonus,
    character.talents,
    character.traits
  );
}

/**
 * Migrate a schema v1 character to v2 with progression tracking
 * @param character - Character to migrate
 * @returns Migrated character with progression tracking, or same character if already migrated
 */
export function migrateCharacterToProgression(character: Character): Character {
  // Don't migrate if already v2 or has progression
  if (character.schemaVersion === 2 || character.progression) {
    return character;
  }

  const progression = createDefaultProgression(
    character.characteristics,
    character.homeworld,
    character.initialWoundsBonus,
    character.talents,
    character.traits
  );

  // Preserve existing insanity and corruption values
  if (character.derivedStats) {
    progression.mentalState.insanityPoints = character.derivedStats.insanity || 0;
    progression.mentalState.corruptionPoints = character.derivedStats.corruption || 0;
    progression.mutationTracking.lastMutationCheck = character.derivedStats.corruption || 0;

    // Preserve existing wounds as current
    if (character.derivedStats.wounds !== undefined) {
      progression.woundsTracking.current = character.derivedStats.wounds;
    }
  }

  return {
    ...character,
    schemaVersion: 2,
    progression,
    lastModified: new Date()
  };
}

export function recalculateProgressionStats(character: Character): Character {
  // If no progression tracking exists, initialize it first
  if (!character.progression) {
    return character;
  }

  const woundsMax = calculateWoundsMaximum(
    character.characteristics,
    character.homeworld,
    character.initialWoundsBonus
  );
  const fatigueMax = calculateFatigueMaximum(character.characteristics);
  const movement = calculateMovementRates(
    character.characteristics,
    character.talents,
    character.traits
  );

  return {
    ...character,
    progression: {
      ...character.progression,
      woundsTracking: {
        ...character.progression.woundsTracking,
        maximum: woundsMax,
        // Ensure current doesn't exceed new maximum
        current: Math.min(character.progression.woundsTracking.current, woundsMax)
      },
      fatigueTracking: {
        ...character.progression.fatigueTracking,
        maximum: fatigueMax
      },
      movementRates: movement
    },
    // Also update derivedStats for backward compatibility
    derivedStats: {
      ...character.derivedStats,
      wounds: woundsMax,
      fatigue: fatigueMax,
      movementHalf: movement.half,
      movementFull: movement.full,
      movementCharge: movement.charge,
      movementRun: movement.run
    }
  };
}

// Derived statistics calculation
export function calculateDerivedStats(character: Character): DerivedStats {
  const { characteristics, homeworld, talents, initialWoundsBonus } = character;

  // Apply homeworld modifiers to characteristics for calculations
  const effectiveCharacteristics = { ...characteristics };
  if (homeworld.characteristicModifiers) {
    Object.entries(homeworld.characteristicModifiers).forEach(([key, modifier]) => {
      if (modifier !== undefined) {
        effectiveCharacteristics[key as keyof Characteristics] += modifier;
      }
    });
  }

  // Calculate base derived stats based on Rogue Trader rules
  let wounds = calculateWoundsMaximum(effectiveCharacteristics, homeworld, initialWoundsBonus);

  let fatigue =
    Math.floor(effectiveCharacteristics.toughness / 10) +
    Math.floor(effectiveCharacteristics.willpower / 10);

  // Apply talent bonuses (multi-purchase talents)
  if (talents && talents.length > 0) {
    talents.forEach((talent) => {
      const purchaseCount = talent.purchaseCount || 1;

      // Sound Constitution: +1 wound per purchase
      if (talent.name === 'Sound Constitution' || talent.baseTalent === 'Sound Constitution') {
        wounds += purchaseCount;
      }

      // Add other multi-purchase talent effects here as needed
      // Example: Iron Jaw might add to fatigue threshold
    });
  }

  // Initiative is Agility Bonus + Perception Bonus in this app. Unnatural
  // Characteristic doubles (or trebles) a Characteristic Bonus, so each half is
  // multiplied independently. Movement is deliberately excluded from this — see
  // calculateMovementRates.
  const initiative =
    Math.floor(effectiveCharacteristics.agility / 10) *
      TraitService.getUnnaturalMultiplier(character.traits, 'agility') +
    Math.floor(effectiveCharacteristics.perception / 10) *
      TraitService.getUnnaturalMultiplier(character.traits, 'perception');

  // Movement went through a second, divergent copy of this calculation here: it
  // omitted Crawler, Quadruped and Unnatural Speed, so derivedStats disagreed
  // with progression.movementRates depending on which function ran last.
  const movement = calculateMovementRates(effectiveCharacteristics, talents, character.traits);

  return {
    wounds,
    fatigue,
    corruption: character.derivedStats?.corruption || 0,
    insanity: character.derivedStats?.insanity || 0,
    profitFactor: character.derivedStats?.profitFactor || 0,
    initiative,
    movementHalf: movement.half,
    movementFull: movement.full,
    movementCharge: movement.charge,
    movementRun: movement.run
  };
}
